<script setup lang="ts">
import {
  h,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  resolveComponent,
  type Component,
} from 'vue'
import AdminSectorForm from '@/views/forms/AdminSectorForm.vue'
import AdminSectorDetail from '@/views/AdminSectorDetail.vue'
import { useAdmin } from '@/stores/admin'
import type { Sector } from '@/stores/admin'
import type { TableColumn } from '@nuxt/ui'
import { handleError } from '@/lib/error'
import { createSortableHeader } from '@/lib/tableHelpers'
import { useUrlFilters } from '@/composables/useUrlFilters'
import { activeFilterUi } from '@/lib/filterUtils'

const admin = useAdmin()
const UBadge = resolveComponent('UBadge')
const UButton = resolveComponent('UButton') as Component

// Create sortable header factory with resolved UButton
const sortable = createSortableHeader(UButton)

// Loading states
const isLoading = ref(true)

// Table sorting state (native UTable/TanStack)
const sorting = ref([{ id: 'name', desc: false }])

// Search
const searchQuery = ref('')

// URL filters
useUrlFilters({
  filters: { search: searchQuery },
  debounce: 300,
})

// Modals and slideovers
const showCreateSectorModal = ref(false)
const showSectorSlideover = ref(false)

// Selected sector for editing
const selectedSector = ref<Sector | null>(null)

// Computed properties
const filteredSectors = computed(() => {
  if (!searchQuery.value) return admin.sectors

  const query = searchQuery.value.toLowerCase()
  return admin.sectors.filter((s) => s.name.toLowerCase().includes(query))
})

// Table columns
const columns: TableColumn<Sector>[] = [
  {
    accessorKey: 'emoji',
    header: '',
    size: 50,
    cell: ({ row }) => {
      const emoji = row.getValue('emoji') as string
      return emoji
        ? h('span', { class: 'text-2xl' }, emoji)
        : h('span', { class: 'text-muted' }, '-')
    },
  },
  {
    accessorKey: 'name',
    header: sortable('Name'),
    size: 200,
  },
  {
    accessorKey: 'brand_count',
    header: sortable('Brands'),
    size: 100,
    cell: ({ row }) => {
      const count = row.getValue('brand_count') as number
      return h(
        UBadge,
        { color: count > 0 ? 'primary' : 'neutral', variant: 'subtle', size: 'md' },
        () => count,
      )
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 100,
    cell: ({ row }) => {
      return h(
        UButton,
        {
          icon: 'i-lucide-settings',
          color: 'neutral',
          variant: 'ghost',
          size: 'sm',
          onClick: () => openSectorSlideover(row.original),
        },
        () => 'Manage',
      )
    },
  },
]

// Initialize
onMounted(async () => {
  window.addEventListener('hashchange', handleHashChange)
  try {
    await Promise.all([admin.fetchSectors(), admin.fetchBrands()])
    // Check for hash to open a specific sector
    const hash = window.location.hash.slice(1)
    if (hash) {
      const sector = admin.sectors.find((s) => s.slug === hash)
      if (sector) {
        openSectorSlideover(sector)
      } else {
        handleError(new Error('Sector not found'), 'Could not find sector')
        history.replaceState(null, '', window.location.pathname)
      }
    }
  } catch (err) {
    handleError(err, 'Failed to load data')
  } finally {
    isLoading.value = false
  }
})

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange)
})

// Clear hash when slideover closes (e.g., clicking outside)
watch(showSectorSlideover, (isOpen) => {
  if (!isOpen && window.location.hash) {
    history.replaceState(null, '', window.location.pathname)
  }
})

// Sector actions
async function handleCreateSector(data: { name: string; slug?: string; emoji?: string }) {
  try {
    const sector = await admin.createSector({ name: data.name, slug: data.slug, emoji: data.emoji })
    showCreateSectorModal.value = false
    // Open the manage slideover for the new sector
    const createdSector = admin.sectors.find((s) => s.id === sector.id)
    if (createdSector) {
      openSectorSlideover(createdSector)
    }
  } catch (err) {
    handleError(err, 'Failed to create sector')
  }
}

function openSectorSlideover(sector: Sector) {
  const isReplacing = showSectorSlideover.value
  selectedSector.value = sector
  showSectorSlideover.value = true
  if (isReplacing) {
    history.replaceState(null, '', `#${sector.slug}`)
  } else {
    history.pushState(null, '', `#${sector.slug}`)
  }
}

function closeSectorSlideover() {
  showSectorSlideover.value = false
  selectedSector.value = null
  if (window.location.hash) {
    history.replaceState(null, '', window.location.pathname)
  }
}

function handleHashChange() {
  const hash = window.location.hash.slice(1)
  if (!hash) {
    if (showSectorSlideover.value) {
      showSectorSlideover.value = false
      selectedSector.value = null
    }
  } else if (selectedSector.value?.slug !== hash) {
    const sector = admin.sectors.find((s) => s.slug === hash)
    if (sector) {
      openSectorSlideover(sector)
    } else {
      handleError(new Error('Sector not found'), 'Could not find sector')
      history.replaceState(null, '', window.location.pathname)
    }
  }
}

function handleSectorUpdated(sector: Sector) {
  selectedSector.value = sector
}

function handleSectorDeleted() {
  closeSectorSlideover()
}

// Refresh data
async function refresh() {
  isLoading.value = true
  try {
    await Promise.all([admin.fetchSectors(), admin.fetchBrands()])
  } catch (err) {
    handleError(err, 'Failed to refresh data')
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <AppComponent title="Sectors" id="admin-sectors">
    <template #actions>
      <span class="text-sm text-muted">{{ admin.sectors.length }} sectors</span>
      <UButton
        icon="i-lucide-refresh-cw"
        color="neutral"
        variant="ghost"
        :loading="isLoading"
        @click="refresh"
      />
      <UButton icon="i-lucide-plus" color="primary" @click="showCreateSectorModal = true">
        Create Sector
      </UButton>
    </template>

    <div class="h-full flex flex-col">
      <!-- Filters -->
      <div class="p-4 space-y-2 shrink-0 bg-[var(--ui-bg)] border-b border-[var(--ui-border)]">
        <div class="flex items-center gap-2">
          <span class="text-sm text-muted w-32">Filter by name</span>
          <UInput
            v-model="searchQuery"
            icon="i-lucide-search"
            placeholder="Search sectors..."
            :ui="searchQuery ? activeFilterUi : undefined"
            class="w-64"
          />
        </div>
      </div>

      <!-- Sectors Table -->
      <div class="flex-1 min-h-0 overflow-auto sticky-table-header">
        <UTable
          :columns="columns"
          :data="filteredSectors"
          :loading="isLoading"
          v-model:sorting="sorting"
        >
          <template #empty>
            <div class="text-center py-6 text-muted">
              {{ isLoading ? 'Loading data...' : 'No data' }}
            </div>
          </template>
        </UTable>
      </div>
    </div>

    <!-- Create Sector Modal -->
    <UModal v-model:open="showCreateSectorModal">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Create Sector</h3>
          </template>
          <AdminSectorForm
            :sector="null"
            @submit="handleCreateSector"
            @cancel="showCreateSectorModal = false"
          />
        </UCard>
      </template>
    </UModal>

    <!-- Sector Management Slideover -->
    <USlideover v-model:open="showSectorSlideover" side="right">
      <template #content>
        <AdminSectorDetail
          :sector="selectedSector"
          @close="closeSectorSlideover"
          @deleted="handleSectorDeleted"
          @updated="handleSectorUpdated"
        />
      </template>
    </USlideover>
  </AppComponent>
</template>
